import React from 'react';
import moment from 'moment';
import { Col, Skeleton, Grid } from 'antd';
import { I18n } from '@aws-amplify/core';
import { Pagination } from '../../../../Pagination';
import { useGetFollowUpPreventiveMeasuresPaginated } from './hooks/useGetFollowUpPreventiveMeasuresPaginated';
import { RiskBadge } from '@/components/views/Resolution886/components/RiskBadge';

import * as S from './styles';

const DEFAULT_TOTAL = 0;

const { useBreakpoint } = Grid;

export function FollowUpPreventiveMeasuresTable(): JSX.Element {
	const { actionPlanDataReview, paginatedActionPlanData, currentPage, limit, setCurrentPage, isLoading } =
		useGetFollowUpPreventiveMeasuresPaginated();

	const screens = useBreakpoint();

	if (isLoading) {
		return (
			<S.TableWrapper>
				<div>
					<h3 style={{ fontSize: '18px', fontWeight: '600' }}>
						{I18n.get('Spreadsheet 4: Follow-up on preventive measures')}
					</h3>
					<S.CustomTable>
						<thead>
							<tr>
								<th style={{ textAlign: 'center' }}>No. M.C.P</th>
								<th style={{ textAlign: 'center' }}>{I18n.get('Action plan name')}</th>
								{screens.xxl && (
									<>
										<th style={{ textAlign: 'center' }}>{I18n.get('Workstation')}</th>
										<th style={{ textAlign: 'center' }}>{I18n.get('Evaluation date')}</th>
									</>
								)}
								<th style={{ textAlign: 'center' }}>{I18n.get('Risk level')}</th>
								<th style={{ textAlign: 'center' }}>
									{I18n.get('Implementation Date (Administrative Measure)')}
								</th>
								<th style={{ textAlign: 'center' }}>
									{I18n.get('Implementation Date (Engineering Measure)')}
								</th>
								{screens.xxl && (
									<th style={{ width: '12%', textAlign: 'center' }}>{I18n.get('Due date')}</th>
								)}
							</tr>
						</thead>
						<tbody>
							{[1, 2, 3, 4, 5, 6].map((index) => (
								<tr key={index}>
									<td style={{ textAlign: 'center' }}>
										<Skeleton.Input active size="small" style={{ width: '30px' }} />
									</td>
									<td style={{ textAlign: 'center' }}>
										<Skeleton.Input active size="default" style={{ width: '120px' }} />
									</td>
									{screens.xxl && (
										<>
											<td style={{ textAlign: 'center' }}>
												<Skeleton.Input active size="default" style={{ width: '100px' }} />
											</td>
											<td style={{ textAlign: 'center' }}>
												<Skeleton.Input active size="small" style={{ width: '80px' }} />
											</td>
										</>
									)}
									<td style={{ textAlign: 'center' }}>
										<Skeleton.Button
											active
											size="default"
											style={{ width: '80px', height: '24px' }}
										/>
									</td>
									<td style={{ textAlign: 'center' }}>
										<Skeleton.Input active size="small" style={{ width: '80px' }} />
									</td>
									<td style={{ textAlign: 'center' }}>
										<Skeleton.Input active size="small" style={{ width: '80px' }} />
									</td>
									{screens.xxl && (
										<td style={{ textAlign: 'center' }}>
											<Skeleton.Input active size="small" style={{ width: '80px' }} />
										</td>
									)}
								</tr>
							))}
						</tbody>
					</S.CustomTable>
					<Col span={24} style={{ marginTop: '20px' }}>
						<Skeleton.Input active size="default" style={{ width: '200px', height: '32px' }} />
					</Col>
				</div>
			</S.TableWrapper>
		);
	}

	return (
		<S.TableWrapper>
			<div>
				<S.CustomTitle>{I18n.get('Spreadsheet 4: Follow-up on preventive measures')}</S.CustomTitle>
				<S.CustomTable>
					<thead>
						<tr>
							<th style={{ textAlign: 'center' }}>No. M.C.P</th>
							<th style={{ textAlign: 'center' }}>{I18n.get('Action plan name')}</th>
							{screens.xxl && (
								<>
									<th style={{ textAlign: 'center' }}>{I18n.get('Workstation')}</th>
									<th style={{ textAlign: 'center' }}>{I18n.get('Evaluation date')}</th>
								</>
							)}
							<th style={{ textAlign: 'center', width: '15%' }}>{I18n.get('Risk level')}</th>
							<th style={{ textAlign: 'center', width: '10%' }}>
								{I18n.get('Implementation Date (Administrative Measure)')}
							</th>
							<th style={{ textAlign: 'center', width: '10%' }}>
								{I18n.get('Implementation Date (Engineering Measure)')}
							</th>
							{screens.xxl && (
								<th style={{ width: '10%', textAlign: 'center' }}>{I18n.get('Due date')}</th>
							)}
						</tr>
					</thead>
					<tbody>
						{paginatedActionPlanData.map((item) => (
							<tr key={item.number}>
								<td style={{ textAlign: 'center' }}>{item.number}</td>
								<td style={{ textAlign: 'center' }}>{item.actionPlanName}</td>
								{screens.xxl && (
									<>
										<td style={{ textAlign: 'center' }}>{item.workstation}</td>
										<td style={{ textAlign: 'center' }}>
											{moment(item.evaluationDate).format('DD/MM/YYYY')}
										</td>
									</>
								)}
								<td style={{ textAlign: 'center' }}>
									<RiskBadge risk={item.riskLevel || '-'} />
								</td>
								<td style={{ textAlign: 'center' }}>
									{item.administrativeImplementationDate
										? moment(item.administrativeImplementationDate).format('DD/MM/YYYY')
										: '-'}
								</td>
								<td style={{ textAlign: 'center' }}>
									{item.engineeringImplementationDate
										? moment(item.engineeringImplementationDate).format('DD/MM/YYYY')
										: '-'}
								</td>
								{screens.xxl && (
									<td style={{ textAlign: 'center' }}>
										{item.dueDate ? moment(item.dueDate).format('DD/MM/YYYY') : '-'}
									</td>
								)}
							</tr>
						))}
					</tbody>
				</S.CustomTable>
				<Col span={24} style={{ marginTop: '20px' }}>
					<Pagination
						total={actionPlanDataReview.length || DEFAULT_TOTAL}
						limit={limit}
						currentPage={currentPage}
						onChangePage={setCurrentPage}
						style={{ justifyContent: 'space-between', width: '100%' }}
					/>
				</Col>
			</div>
		</S.TableWrapper>
	);
}
